import React, { useState, useEffect, useRef } from 'react';
import './WordsGame.css';
import api from '../../../services/axiosConfig';
import { useNavigate } from 'react-router-dom';
import DraggableWord from '../../../components/DraggableWord';
import DropZone from '../../../components/DropZone';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// Интерфейсы для типов данных
export interface Word {
  id: number;
  word_udi: string;
  translation: string;
  audio_url?: string;
}

interface UserStats {
  totalLearned: number;
  masteredCount: number;
  needReviewCount: number;
}

interface WordsGameProps {
  setInitialRect: (rect: DOMRect) => void;
}

// Количество повторов для новых слов (режим "new" и "all")
const REPEAT_COUNT = 3;

// Импорт звуков обратной связи
import correctSoundFile from '../../../assets/right.wav';
import incorrectSoundFile from '../../../assets/wrong.wav';

// Импорт иконок обратной связи
import correctIconImg from '../../../assets/right-icon.png';
import incorrectIconImg from '../../../assets/wrong-icon.png';

const CorrectIcon = () => (
  <img src={correctIconImg} alt="Правильно" className="feedback-icon" />
);
const IncorrectIcon = () => (
  <img src={incorrectIconImg} alt="Неправильно" className="feedback-icon" />
);

// Функция перемешивания массива
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Генерация вариантов ответов
const generateOptions = (correctTranslation: string, allWords: Word[]): string[] => {
  const normalizedCorrect = correctTranslation.split(',')[0].trim().toLowerCase();
  const otherTranslations = allWords
    .map((w) => w.translation.split(',')[0].trim().toLowerCase())
    .filter((trans) => trans && trans !== normalizedCorrect);
  let randomOption = '';
  if (otherTranslations.length > 0) {
    const index = Math.floor(Math.random() * otherTranslations.length);
    randomOption = otherTranslations[index];
  }
  const opts = [normalizedCorrect];
  if (randomOption) opts.push(randomOption);
  if (opts.length === 1) {
    opts.push(normalizedCorrect);
  }
  return shuffleArray(opts);
};

const WordsGame: React.FC<WordsGameProps> = ({ setInitialRect }) => {
  const navigate = useNavigate();

  // Состояния для профиля, статистики и слов
  const [username, setUsername] = useState<string | null>(null);
  const [wordStats, setWordStats] = useState<UserStats | null>(null);
  const [userWordProgress, setUserWordProgress] = useState<{ wordId: number; status: string }[]>([]);
  const [allWords, setAllWords] = useState<Word[]>([]);
  const [error, setError] = useState<string>('');

  // Состояния для выбора режима и длительности тренировки  
  // Три категории: 'review', 'new', 'all'
  // Допустимые длительности: 120 сек (2 минуты) и 300 сек (5 минут)
  const [mode, setMode] = useState<string>(''); // изначально не выбрано
  const [sessionDuration, setSessionDuration] = useState<number>(0); // изначально не выбрано

  // Состояния тренировки
  const [started, setStarted] = useState<boolean>(false);
  const [sessionEnded, setSessionEnded] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [sessionQueue, setSessionQueue] = useState<Word[]>([]);
  const [currentWord, setCurrentWord] = useState<Word | null>(null);
  const [options, setOptions] = useState<string[]>([]);

  // Локальная статистика тренировки
  const [sessionWordStats, setSessionWordStats] = useState<Record<number, { correct: number; incorrect: number }>>({});
  const [sessionCorrectCount, setSessionCorrectCount] = useState<number>(0);
  const [sessionIncorrectCount, setSessionIncorrectCount] = useState<number>(0);

  // Состояния для аудио и визуальной обратной связи
  const [feedbackType, setFeedbackType] = useState<string>('');
  const [preloadedAudios, setPreloadedAudios] = useState<{ [id: number]: HTMLAudioElement }>({});

  // Для хранения таймера
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Функция воспроизведения аудио слова
  const playAudio = (audioUrl?: string, wordId?: number) => {
    if (!audioUrl || !started) return;
    let audio: HTMLAudioElement;
    if (wordId && preloadedAudios[wordId]) {
      audio = preloadedAudios[wordId];
    } else {
      const fullAudioUrl = audioUrl.startsWith('http')
        ? audioUrl
        : `${import.meta.env.VITE_API_URL || 'https://udilang.ru'}${audioUrl.startsWith('/') ? '' : '/'}${audioUrl}`;
      audio = new Audio(fullAudioUrl);
      audio.preload = 'auto';
    }
    audio.play().catch((err) => {
      console.error('Ошибка при воспроизведении аудио:', err);
    });
  };

  // Функция воспроизведения звука обратной связи
  const playFeedbackSound = (isCorrect: boolean) => {
    const audioSrc = isCorrect ? correctSoundFile : incorrectSoundFile;
    const audio = new Audio(audioSrc);
    audio.play().catch((err) => {
      console.error('Ошибка при воспроизведении звука обратной связи:', err);
    });
  };

  // Предзагрузка аудио для следующего слова
  const preloadAudioForWord = (word: Word) => {
    if (!word.audio_url) return;
    if (preloadedAudios[word.id]) return;
    const fullAudioUrl = word.audio_url.startsWith('http')
      ? word.audio_url
      : `${import.meta.env.VITE_API_URL || 'https://udilang.ru'}${word.audio_url.startsWith('/') ? '' : '/'}${word.audio_url}`;
    const audio = new Audio(fullAudioUrl);
    audio.preload = 'auto';
    audio.load();
    setPreloadedAudios((prev) => ({ ...prev, [word.id]: audio }));
  };

  // Получение данных (профиль, статистика, прогресс, слова)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    const fetchData = async () => {
      try {
        const profileRes = await api.get('/user/profile');
        setUsername(profileRes.data.username);
        const statsRes = await api.get('/user/stats');
        setWordStats(statsRes.data);
        const progressRes = await api.get('/user/user_word_progress');
        setUserWordProgress(progressRes.data);
        const wordsRes = await api.get('/dictionary');
        const fetchedWords: Word[] = wordsRes.data.map((item: any) => ({
          id: item.id,
          word_udi: item.word_udi || '',
          translation: item.word_rus,
          audio_url: item.audio_url,
        }));
        const filteredWords = fetchedWords.filter(
          (word) =>
            word.word_udi.trim() !== '' &&
            word.translation.trim() !== '' &&
            word.audio_url &&
            word.audio_url.trim() !== ''
        );
        setAllWords(filteredWords);
      } catch (err: any) {
        if (err.response) {
          setError(`Ошибка: ${err.response.status} - ${err.response.data.message}`);
        } else if (err.request) {
          setError('Ошибка: Нет ответа от сервера');
        } else {
          setError(`Ошибка: ${err.message}`);
        }
        console.error(err);
      }
    };
    fetchData();
  }, [navigate]);

  // Таймер тренировки: обратный отсчёт
  useEffect(() => {
    if (started && !sessionEnded) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
            endSession();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [started, sessionEnded]);

  // Функция, инициализирующая очередь тренировки  
  // Для режима "new" и "all": для 2 мин – 10 слов, для 5 мин – 20 слов (с повторениями);
  // Для режима "review": для 2 мин – 20 слов, для 5 мин – 40 слов (без повторений)
  const initializeTrainingQueue = () => {
    if (allWords.length === 0) return;
    let filteredWords: Word[] = [];
    if (mode === 'new') {
      // Новые слова – те, которых нет в прогрессе
      filteredWords = allWords.filter(
        (word) => !userWordProgress.some((prog) => prog.wordId === word.id)
      );
    } else if (mode === 'review') {
      // Повторять: все слова, которые учил пользователь, кроме освоенных
      filteredWords = allWords.filter((word) => {
        const prog = userWordProgress.find((p) => p.wordId === word.id);
        return prog && prog.status !== 'mastered';
      });
    } else if (mode === 'all') {
      filteredWords = allWords;
    }
    // Если в выбранном режиме нет слов, выводим сообщение и сбрасываем выбор
    if (filteredWords.length === 0) {
      if (mode === 'new') {
        setError('Пока нет новых слов для обучения.');
      } else if (mode === 'review') {
        setError('Вы уже освоили все слова');
      } else {
        setError('Выберите режим');
      }
      resetToInitialScreen();
      return;
    }
    let distinctCount = 0;
    if (mode === 'review' || mode === 'all') {
      distinctCount = sessionDuration === 120 ? 20 : sessionDuration === 300 ? 40 : 0;
    } else {
      distinctCount = sessionDuration === 120 ? 10 : sessionDuration === 300 ? 20 : 0;
    }
    const shuffled = shuffleArray(filteredWords);
    const selectedDistinct = shuffled.slice(0, distinctCount);
    let trainingRounds: Word[] = [];
    if (mode === 'review' || mode === 'all') {
      // Для режимов "review" и "all" слова показываются по одному разу,
      // и при ошибке слово повторяется (поведение в goToNextRound)
      trainingRounds = shuffleArray(selectedDistinct);
    } else {
      selectedDistinct.forEach((word) => {
        for (let i = 0; i < REPEAT_COUNT; i++) {
          trainingRounds.push(word);
        }
      });
      trainingRounds = shuffleArray(trainingRounds);
    }
    setSessionQueue(trainingRounds);
    setCurrentWord(trainingRounds[0] || null);
    if (trainingRounds[0]) {
      preloadAudioForWord(trainingRounds[0]);
    }
  };

  // Следим за изменением очереди тренировки (работает только если тренировка запущена)
  useEffect(() => {
    if (!started) return;
    if (sessionQueue.length > 0) {
      setCurrentWord(sessionQueue[0]);
      if (sessionQueue[1]) {
        preloadAudioForWord(sessionQueue[1]);
      }
    } else {
      endSession();
    }
  }, [sessionQueue, started]);

  // При смене currentWord: обновляем варианты ответа и воспроизводим аудио
  useEffect(() => {
    if (currentWord && started) {
      const opts = generateOptions(currentWord.translation, allWords);
      setOptions(opts);
      setFeedbackType('');
      playAudio(currentWord.audio_url, currentWord.id);
    }
  }, [currentWord, allWords, started]);

  // Функция завершения тренировки
  const endSession = () => {
    setSessionEnded(true);
    setStarted(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  // Обновление локальной статистики для слова
  const updateSessionWordStats = (wordId: number, isCorrect: boolean) => {
    setSessionWordStats((prev) => {
      const stats = prev[wordId] || { correct: 0, incorrect: 0 };
      const updated = isCorrect
        ? { ...stats, correct: stats.correct + 1 }
        : { ...stats, incorrect: stats.incorrect + 1 };
      return { ...prev, [wordId]: updated };
    });
  };

  // Логика обработки ответа пользователя
  const handleAnswer = (selected: string) => {
    if (!currentWord) return;
    const correctAnswer = currentWord.translation.split(',')[0].trim().toLowerCase();
    const selectedAnswer = selected.split(',')[0].trim().toLowerCase();
    const isCorrect = selectedAnswer === correctAnswer;
    playFeedbackSound(isCorrect);
    setFeedbackType(isCorrect ? 'correct' : 'incorrect');
    updateSessionWordStats(currentWord.id, isCorrect);
    if (isCorrect) {
      setSessionCorrectCount((prev) => prev + 1);
    } else {
      setSessionIncorrectCount((prev) => prev + 1);
    }
    const body = {
      wordId: currentWord.id,
      result: isCorrect ? 'correct' : 'incorrect',
    };
    api.post('/dictionary/progress', body).catch((err) => {
      console.error('Ошибка при обновлении прогресса слова:', err);
    });
    setTimeout(() => {
      goToNextRound(isCorrect);
    }, isCorrect ? 1000 : 750);
  };

  // Переход к следующему раунду.
  // В режиме "review": если ответ верный – слово удаляется; если неверный – слово добавляется в конец очереди.
  // В остальных режимах – удаляем все копии текущего слова (как раньше).
  const goToNextRound = (justAnsweredCorrectly: boolean) => {
    if (!currentWord) return;
    if (mode === 'review') {
      setSessionQueue((prevQueue) => {
        const newQueue = prevQueue.slice(1);
        if (!justAnsweredCorrectly) {
          // Добавляем слово в конец очереди, чтобы оно появилось позже
          newQueue.push(currentWord);
        }
        return newQueue;
      });
    } else {
      const stats = sessionWordStats[currentWord.id] || { correct: 0, incorrect: 0 };
      const currentCorrect = stats.correct + (justAnsweredCorrectly ? 1 : 0);
      setSessionQueue((prevQueue) => {
        let newQueue = prevQueue.slice(1);
        while (newQueue.length > 0 && newQueue[0].id === currentWord.id) {
          newQueue = newQueue.slice(1);
        }
        return newQueue;
      });
    }
  };

  // Обработка дропа
  const handleDrop = (target: string) => {
    if (target === 'dontknow') {
      handleAnswer('не знаю');
    } else {
      handleAnswer(target);
    }
  };

  // Функция для запуска тренировки (при выборе "Начать игру")
  const startTraining = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSessionEnded(false);
    setStarted(true);
    setTimeLeft(sessionDuration);
    setSessionQueue([]);
    setCurrentWord(null);
    setSessionWordStats({});
    setSessionCorrectCount(0);
    setSessionIncorrectCount(0);
    setFeedbackType('');
    initializeTrainingQueue();
  };

  // Функция для завершения игры (кнопка "Завершить игру")
  const finishGame = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    endSession()
  };

  // Функция для сброса тренировки и возвращения к начальному экрану
  const resetToInitialScreen = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSessionEnded(false);
    setStarted(false);
    setTimeLeft(0);
    setSessionQueue([]);
    setCurrentWord(null);
    setSessionWordStats({});
    setSessionCorrectCount(0);
    setSessionIncorrectCount(0);
    setFeedbackType('');
  };

  // Рендеринг

  // Экран загрузки данных
  if (allWords.length === 0) {
    return (
      <div className='start-screen-scelet'>
        <p style={{ marginBottom: '6px'}}>
          <Skeleton width={300} height={22} borderRadius={12} />
        </p>
        <div style={{ marginBottom: '24px', display: 'flex', gap: '8px'  }}>
          <Skeleton height={150} width={'100%'} borderRadius={12} containerClassName="flex-1" />
          <Skeleton height={150} width={'100%'} borderRadius={12} containerClassName="flex-1" />
        </div>
        <p style={{ marginBottom: '6px'}}>
          <Skeleton width={300} height={22} borderRadius={12} />
        </p>
        <div style={{ marginBottom: '24px', display: 'flex', gap: '8px'  }}>
          <Skeleton height={150} width={'100%'} borderRadius={12} containerClassName="flex-1" />
          <Skeleton height={150} width={'100%'} borderRadius={12} containerClassName="flex-1" />
        </div>
        <p style={{ marginBottom: '6px'}}>
          <Skeleton width={300} height={22} borderRadius={12} />
        </p>
        <div style={{ marginBottom: '24px', display: 'flex', gap: '8px'  }}>
          <Skeleton height={150} width={'100%'} borderRadius={12} containerClassName="flex-1" />
          <Skeleton height={150} width={'100%'} borderRadius={12} containerClassName="flex-1" />
        </div>
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'center'  }}>
         <Skeleton height={50} width={235} borderRadius={12} />
        </div>
       
      </div>
    );
  }
   // Если тренировка завершена – вывод итогового результата тренировки
   if (sessionEnded) {
    return (
      <div className="games-result-container">
        <div className="session-summary">
          <h1>Отлично!</h1>
          <p className='game-end-message'>Тренировка завершена</p>
          <p>Правильных ответов: {sessionCorrectCount}</p>
          <p>Неправильных ответов: {sessionIncorrectCount}</p>
          <button className="words-restart-btn" onClick={resetToInitialScreen}>
            Вернуться в меню
          </button>
        </div>
      </div>
    );
  }

  // Если тренировка не запущена – отображаем стартовый экран с выбором режимов и длительности.
  if (!started) {
    return (
      <div className="start-screen">
        {error && <p className="error-message">{error}</p>}
        <div className="mode-selection">
        <h4 className='section-title mode-card-title'>Учить новые слова</h4>

        <div className="card-row">
          <div
            className={`card ${mode === 'new' && sessionDuration === 120 ? 'selected' : ''}`}
            onClick={() => { setError(''); setMode('new'); setSessionDuration(120); }}
          >
            <p className='mode-descr mode-words-count'>10 слов</p>
            <p className='mode-descr'>Узнайте новые слова и закрепите их</p>
            <span className='mode-time'>2 мин</span>
            <span className='new-icon'></span>

          </div>
          <div
            className={`card ${mode === 'new' && sessionDuration === 300 ? 'selected' : ''}`}
            onClick={() => { setError(''); setMode('new'); setSessionDuration(300); }}
          >
            <p className='mode-descr mode-words-count'>20 слов</p>
            <p className='mode-descr'>Узнайте новые слова и закрепите их</p>
            <span className='mode-time'>5 мин</span>
            <span className='new-icon'></span>

          </div>
        </div>
          <h4 className='section-title mode-card-title'>Повторять слова</h4>
          <div className="card-row">
            <div
              className={`card ${mode === 'review' && sessionDuration === 120 ? 'selected' : ''}`}
              onClick={() => { setError(''); setMode('review'); setSessionDuration(120); }}
            >
              <p className='mode-descr mode-words-count'>20 слов</p>
              <p className='mode-descr'>Только слова, которые вы уже проходили</p>
              <span className='mode-time'>2 мин</span>
              <span className='repeat-icon'></span>
            </div>
            <div
              className={`card ${mode === 'review' && sessionDuration === 300 ? 'selected' : ''}`}
              onClick={() => { setError(''); setMode('review'); setSessionDuration(300); }}
            >
              <p className='mode-descr mode-words-count'>40 слов</p>
              <p className='mode-descr'>Только слова, которые вы уже проходили</p>
              <span className='mode-time'>5 мин</span>
              <span className='repeat-icon'></span>
            </div>
          </div>
          
          <h4 className='section-title mode-card-title'>Все слова вперемешку</h4>
          <div className="card-row">
            <div
              className={`card ${mode === 'all' && sessionDuration === 120 ? 'selected' : ''}`}
              onClick={() => { setError(''); setMode('all'); setSessionDuration(120); }}
            >
              
              <p className='mode-descr mode-words-count'>20 слов</p>
              <p className='mode-descr'>Повторяйте то, что знаете и учите новое</p>
              <span className='mode-time'>2 мин</span>
              <span className='mix-icon'></span>

            </div>
            <div
              className={`card ${mode === 'all' && sessionDuration === 300 ? 'selected' : ''}`}
              onClick={() => { setError(''); setMode('all'); setSessionDuration(300); }}
            >
              <p className='mode-descr mode-words-count'>40 слов</p>
              <p className='mode-descr'>Повторяйте то, что знаете и учите новое</p>
              <span className='mode-time'>5 мин</span>
              <span className='mix-icon'></span>

            </div>
          </div>
          <div className="words-btn-wrapper">
            <button className="words-start-btn" onClick={startTraining}>
              Начать тренировку
            </button>
          </div>
          
        </div>
        
      </div>
    );
  }

  // Экран самой игры (с активным обратным отсчётом) с кнопкой "Завершить игру"
  return (
    <div className="games-container">
      <div className="container">
        <div className="top-bar">
          <div className="timer">{timeLeft} сек</div>
          <button className="end-btn" onClick={finishGame}>
          </button>
        </div>
        {currentWord ? (
          <>
            <DraggableWord
              word={currentWord.word_udi.toLowerCase()}
              setInitialRect={setInitialRect}
            />
            <button
              className="listen-btn"
              onClick={() => playAudio(currentWord.audio_url, currentWord.id)}
            >
              Прослушать ещё раз
            </button>
            <div className="options-wrapper">
              <DropZone target={options[0] || ''} onDrop={handleDrop}>
                {options[0] || ''}
              </DropZone>
              <DropZone target="dontknow" onDrop={handleDrop}>
                не знаю
              </DropZone>
              <DropZone target={options[1] || ''} onDrop={handleDrop}>
                {options[1] || ''}
              </DropZone>
            </div>
            <div className="feedback">
              {feedbackType === 'correct' && <CorrectIcon />}
              {feedbackType === 'incorrect' && <IncorrectIcon />}
            </div>
          </>
        ) : (
          <p>Загружается следующее слово...</p>
        )}
      </div>
    </div>
  );
};

export default WordsGame;
